// Review follow-up (Story 7.15 / ADO-1372, Peter Eastwood 2026-08-19) — a
// catalogue-linking SiteHeader that ALSO carries the search typeahead (Story 7.2)
// and the barcode-scan control (Story 7.18).
//
// Both controls already lived in SiteHeader and worked on the /products catalogue
// page, but the shared `useCatalogueHeader()` bundle wired a plain submit-only search
// (no `searchTypeahead` slot, no `onScanClick`), so on the PDP — a shopping surface
// that links to the catalogue — the header had neither: typing produced no dropdown
// and there was no scan icon. This wrapper composes `useCatalogueHeader()` with that
// missing wiring, matching the /products header (`Products.tsx`).
//
// SCOPE (ADO-1779): every signed-in page outside /products uses it (the PDP, My
// Account, Previous orders, Admin users), so the header carries the same search,
// camera and Browse controls wherever it appears, as the Figma header does.
// /products composes the same controls itself (Products.tsx).
//
// A typeahead commit (a suggestion, a recent term or "View all") hands the term to
// the catalogue exactly as a submit does — Story 7.35 (ADO-2032): navigate to
// `/products?q=<term>`, a shareable canonical URL the /products mount restores and
// runs, replacing the former transient `location.state` handover.

import { type ReactNode, useId, useRef, useState } from 'react';
import { SiteHeader } from '../../design-system';
import { useCatalogueHeader } from './useHeaderChrome';
import { SearchTypeahead } from './SearchTypeahead';
import { useBrowseMegaMenu } from '../Browse/useBrowseMegaMenu';
import { ScanModal } from '../Scan/ScanModal';
import { detectScanCapability } from '../Scan/scanCapability';

export interface CatalogueLinkingHeaderProps {
  /** Manager-dashboard link gate (Story 6.31) — the caller passes its own
   *  role check, exactly as it did when spreading `useCatalogueHeader()` directly. */
  readonly showManagerDashboard: boolean;
  /** Overlay slot beneath the sub-nav (unused today; kept for parity with the
   *  SiteHeader contract). */
  readonly children?: ReactNode;
  /** Story 7.33 (ADO-2026): the header's "Planogram" entry, where the host offers it. */
  readonly planogramNav?: { readonly to: string } | undefined;
}

export function CatalogueLinkingHeader({
  showManagerDashboard,
  children,
  planogramNav,
}: CatalogueLinkingHeaderProps) {
  const { submitSearch, ...header } = useCatalogueHeader();

  // Browse opens the department mega-menu in place (not a route to /products) so
  // it behaves the same here as on the catalogue page; selecting a node hands off
  // to /products.
  const browse = useBrowseMegaMenu();

  // The dropdown opens on focus/keystroke and closes on commit/clear (here) or
  // Escape/click-outside (inside SearchTypeahead). `typeaheadGroupId` is the group's
  // DOM id (a labelled group of buttons, not an ARIA combobox listbox — PR #139).
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const typeaheadGroupId = useId();

  // Camera barcode scan (Story 7.18) — capability-gated once per mount; the flow
  // lives entirely in ScanModal, a separate surface from the search box, so a scanned
  // barcode NEVER lands in the search field.
  const [scanOpen, setScanOpen] = useState(false);
  const [scanCapability] = useState(detectScanCapability);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // A typeahead commit goes through the header's one search sender, which records the term
  // before navigating (PR #864 review; a pasted `?q=` link records nothing).
  const commit = (term: string) => {
    setTypeaheadOpen(false);
    submitSearch(term);
  };

  return (
    <>
      <SiteHeader
        {...header}
        // Browse is the in-place mega-menu disclosure (not a route to /products).
        onBrowseClick={browse.onBrowseClick}
        browseExpanded={browse.browseExpanded}
        browseMenuId={browse.browseMenuId}
        onBrowseDismiss={browse.onBrowseDismiss}
        showManagerDashboard={showManagerDashboard}
        planogramNav={planogramNav}
        searchInputRef={searchInputRef}
        // Wrap the base handlers so every keystroke / focus (re)opens the dropdown and
        // a clear closes it, exactly like the /products header.
        onSearchChange={(value) => {
          header.onSearchChange(value);
          setTypeaheadOpen(true);
        }}
        onSearchFocus={() => setTypeaheadOpen(true)}
        onClearSearch={() => {
          header.onClearSearch();
          setTypeaheadOpen(false);
        }}
        searchTypeahead={
          <SearchTypeahead
            query={header.searchValue}
            open={typeaheadOpen}
            listboxId={typeaheadGroupId}
            onClose={() => setTypeaheadOpen(false)}
            onCommit={commit}
          />
        }
        onScanClick={() => setScanOpen(true)}
        scanAvailable={scanCapability.available}
        scanUnavailableReason={scanCapability.reason}
        scanActive={scanOpen}
      >
        {browse.browseOverlay}
        {children}
      </SiteHeader>

      <ScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        // "Search by name" fallback (AC7/AC12): the modal closes first (restoring focus
        // to the trigger), so defer the focus a frame to land it on the search input.
        onUseTextSearch={() => requestAnimationFrame(() => searchInputRef.current?.focus())}
      />
    </>
  );
}

export default CatalogueLinkingHeader;
